import { DatePipe } from '@angular/common';
import { inject, Injectable } from '@angular/core';
import { FilterActionType } from '@common/enums';
import { UserReport } from '@common/models';
import { SortOrderByPipe } from '@common/pipes';
import { ReportsService } from '@common/services';
import { getUniqueListByProp, ReportsUserTag, ReportsUserTagsMap } from '@common/services/smart-search';
import SmartSearchTemplatesBase from '@common/services/smart-search/smart-search-template-base';
import { createFilterOptions, getFilterByContains, getFilterByDate } from '@common/utils/functions/search';
import cloneDeep from 'lodash/cloneDeep';
import { ModelTemplate } from 'mbs-ui-kit';
import { asapScheduler, Observable, scheduled } from 'rxjs';
import { map, tap } from 'rxjs/operators';

@Injectable()
export class SmartSearchModelTemplateReportsService extends SmartSearchTemplatesBase {
  private readonly datePipe = inject(DatePipe);
  private readonly reportsService = inject(ReportsService);
  private readonly sortOrderByPipe = inject<SortOrderByPipe<UserReport>>(SortOrderByPipe);
  private readonly userReport$ = this.reportsService.userReports().pipe(map((res) => res.data));

  readonly #license = ReportsUserTagsMap.get(ReportsUserTag.License);
  readonly #userName = ReportsUserTagsMap.get(ReportsUserTag.UserName);
  readonly #userState = ReportsUserTagsMap.get(ReportsUserTag.UserState);

  private keyCache = 'reports-name_';

  public readonly UserName: ModelTemplate<UserReport> = {
    tag: this.#userName.tag,
    items: (state): Observable<UserReport[]> => {
      const term = state.leftCaretValue;
      const options = createFilterOptions(term, this.#userName.prop);

      this.reportsService.filter = getFilterByContains(options) || '';

      // if (this.cache[this.keyCache + term]) {
      //   return of(this.cache[this.keyCache + term]) as Observable<UserReport[]>;
      // }

      return scheduled(
        this.userReport$.pipe(
          map((reports) => {
            const filtered = reports.filter((report) => report.UserName != null && report.UserName.includes(term));
            const uniqueList = getUniqueListByProp(filtered, this.#userName.prop).slice(0, 10);

            return this.sortOrderByPipe.transform(uniqueList, this.#userName.prop);
          }),
          tap<UserReport[]>(this.writeToCache(this.keyCache + term))
        ),
        asapScheduler
      );
    },
    itemFormatter: ({ UserName }): string => UserName,
    addGroupBrackets: true
  };

  public readonly License: ModelTemplate<UserReport> = {
    tag: this.#license.tag,
    items: (state): Observable<UserReport[]> => {
      const term = state.leftCaretValue;
      const options = createFilterOptions(term, this.#license.prop);

      this.reportsService.filter = getFilterByDate(options) || '';

      // if (this.cache[this.keyCache + term]) {
      //   return of(this.cache[this.keyCache + term]) as Observable<UserReport[]>;
      // }

      return scheduled(
        this.userReport$.pipe(
          map((reports) => {
            const cloneInvoices = cloneDeep(reports).map((report) => ({
              ...report,
              LicenseExpireDate: this.datePipe.transform(report.LicenseExpireDate)
            }));
            const filtered = cloneInvoices.filter((report) => report.LicenseExpireDate?.includes(term));
            const uniqueList = getUniqueListByProp(filtered, this.#license.prop).slice(0, 10);

            return this.sortOrderByPipe.transform(uniqueList, this.#license.prop, 'Date');
          }),
          tap<UserReport[]>(this.writeToCache(this.keyCache + term))
        ),
        asapScheduler
      );
    },
    itemFormatter: ({ LicenseExpireDate }): string => this.datePipe.transform(LicenseExpireDate, 'mediumDate'),
    addGroupBrackets: true
  };

  public readonly UserState: ModelTemplate<UserReport> = {
    tag: this.#userState.tag,
    items: (state): Observable<UserReport[]> => {
      const term = state.leftCaretValue;

      this.reportsService.filter = term || '';

      // if (this.cache[this.keyCache + term]) {
      //   return of(this.cache[this.keyCache + term]) as Observable<UserReport[]>;
      // }

      return scheduled(
        this.userReport$.pipe(
          map((reports) => {
            const filtered = reports.filter((report) => report.InBackup != null);

            return getUniqueListByProp(filtered, this.#userState.prop).slice(0, 10);
          }),
          tap<UserReport[]>(this.writeToCache(this.keyCache + term))
        ),
        asapScheduler
      );
    },
    itemFormatter: ({ InBackup }): string => (InBackup ? FilterActionType.EnabledUsers : FilterActionType.DisabledUsers),
    addGroupBrackets: true
  };
}
